import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grow,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
  Buildings,
  Globe,
  type Icon,
  Lock,
  Plus,
  SquaresFour,
  X,
} from '@phosphor-icons/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { StyledTextField } from '../../Common/StyledTextField';

type DashboardCreateVisibility = 'public' | 'workspace' | 'private';

interface VisibilityOptionDefinition {
  value: DashboardCreateVisibility;
  label: string;
  description: string;
  disabled: boolean;
  Icon: Icon;
  accentColor: string;
  iconBg: string;
  iconBorder: string;
}

function buildVisibilityOptions(theme: Theme): VisibilityOptionDefinition[] {
  const successMain = theme.palette.success.main;
  const errorMain = theme.palette.error.main;
  const blueMain = theme.customColorPalette.blue[500];
  return [
    {
      value: 'public',
      label: 'Organization',
      description: 'All members of your organization can view this dashboard.',
      disabled: false,
      Icon: Globe,
      accentColor: successMain,
      iconBg: alpha(successMain, 0.12),
      iconBorder: alpha(successMain, 0.28),
    },
    {
      value: 'workspace',
      label: 'Workspaces',
      description:
        'Only members of selected workspaces can access this dashboard.',
      disabled: true,
      Icon: Buildings,
      accentColor: blueMain,
      iconBg: alpha(blueMain, 0.1),
      iconBorder: alpha(blueMain, 0.25),
    },
    {
      value: 'private',
      label: 'Private',
      description: 'Only you can see and access this dashboard.',
      disabled: true,
      Icon: Lock,
      accentColor: errorMain,
      iconBg: alpha(errorMain, 0.08),
      iconBorder: alpha(errorMain, 0.22),
    },
  ];
}

export interface CreateDashboardModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void> | void;
  isCreating: boolean;
  showAccessPermissionOptions: boolean;
}

function SectionLabel({
  children,
  mutedColor,
}: {
  children: React.ReactNode;
  mutedColor: string;
}) {
  return (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: mutedColor,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

export const CreateDashboardModal: React.FC<CreateDashboardModalProps> = ({
  open,
  onClose,
  onCreate,
  isCreating,
  showAccessPermissionOptions,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const visibilityOptions = useMemo(
    () => buildVisibilityOptions(theme),
    [theme],
  );

  const [name, setName] = useState('');
  const [nameErr, setNameErr] = useState(false);
  const [visibility, setVisibility] =
    useState<DashboardCreateVisibility>('public');

  useEffect(() => {
    if (!open) {
      setName('');
      setNameErr(false);
      setVisibility('public');
    }
  }, [open]);

  const textPrimary = isDarkMode ? palette.gray[100] : palette.gray[900];
  const textSecondary = isDarkMode ? palette.gray[400] : palette.gray[500];
  const mutedSection = isDarkMode ? palette.gray[500] : palette.gray[500];
  const paperBg = isDarkMode ? palette.gray[900] : '#fff';
  const borderSubtle = isDarkMode ? palette.gray[700] : palette.gray[300];
  const cardHoverBg = isDarkMode ? palette.gray[800] : palette.gray[100];

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameErr(true);
      return;
    }
    await onCreate(trimmed);
  };

  const handleVisibilityChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    const next = value as DashboardCreateVisibility;
    const opt = visibilityOptions.find(o => o.value === next);
    if (opt?.disabled) return;
    setVisibility(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      TransitionComponent={Grow}
      TransitionProps={{
        style: { transformOrigin: 'center center' },
        timeout: { enter: 240, exit: 220 },
        easing: {
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.22, 1, 0.36, 1)',
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: paperBg,
          backgroundImage: 'none',
          border: `1px solid ${borderSubtle}`,
          borderRadius: 2,
          width: 460,
          maxWidth: '96vw',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <SquaresFour
            size={18}
            weight='bold'
            color={theme.palette.primary.main}
          />
          <Typography
            sx={{ fontWeight: 600, fontSize: 15, color: textPrimary }}
          >
            Create dashboard
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: textSecondary }}>
          {showAccessPermissionOptions
            ? 'Name your dashboard and set who can access it.'
            : 'Enter a name for your dashboard.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important' }}>
        <SectionLabel mutedColor={mutedSection}>Dashboard name</SectionLabel>
        <StyledTextField
          autoFocus
          fullWidth
          size='small'
          placeholder='e.g. Q2 Marketing Hub'
          value={name}
          error={nameErr}
          helperText={nameErr ? 'Please enter a name' : ''}
          FormHelperTextProps={{
            sx: { fontSize: 11, color: nameErr ? undefined : textSecondary },
          }}
          onChange={e => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameErr(false);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !isCreating) {
              void handleSubmit();
            }
          }}
          sx={{ mb: showAccessPermissionOptions ? 2.5 : 0 }}
        />

        {showAccessPermissionOptions ? (
          <>
            <SectionLabel mutedColor={mutedSection}>
              Access permission
            </SectionLabel>
            <RadioGroup value={visibility} onChange={handleVisibilityChange}>
              <Stack spacing={1}>
                {visibilityOptions.map(
                  ({
                    value,
                    label,
                    description,
                    disabled,
                    Icon: OptIcon,
                    accentColor,
                    iconBg,
                    iconBorder,
                  }) => {
                    const sel = visibility === value;
                    const cardInner = (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (!disabled) setVisibility(value);
                        }}
                        sx={{
                          p: 1.5,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          border: '1.5px solid',
                          borderColor: sel ? accentColor : borderSubtle,
                          borderRadius: 2,
                          bgcolor: sel ? iconBg : 'transparent',
                          opacity: disabled ? 0.5 : 1,
                          transition:
                            'border-color 0.15s ease, background-color 0.15s ease',
                          ...(!disabled && {
                            '&:hover': {
                              borderColor: sel ? accentColor : borderSubtle,
                              bgcolor: sel ? iconBg : cardHoverBg,
                            },
                          }),
                        }}
                      >
                        <FormControlLabel
                          value={value}
                          disabled={disabled}
                          control={
                            <Radio
                              size='small'
                              disabled={disabled}
                              sx={{
                                p: 0,
                                mr: 1.5,
                                color: isDarkMode
                                  ? 'rgba(255,255,255,0.18)'
                                  : palette.gray[400],
                                '&.Mui-checked': { color: accentColor },
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: '10px',
                                  bgcolor: iconBg,
                                  border: `1px solid ${iconBorder}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <OptIcon
                                  size={20}
                                  weight='bold'
                                  color={accentColor}
                                />
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: textPrimary,
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: textSecondary,
                                  }}
                                >
                                  {description}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ m: 0, width: '100%', alignItems: 'center' }}
                        />
                      </Paper>
                    );

                    if (disabled) {
                      return (
                        <Tooltip
                          key={value}
                          title='Coming soon'
                          placement='top'
                          arrow
                        >
                          <Box
                            component='span'
                            sx={{
                              display: 'block',
                              width: '100%',
                              cursor: 'not-allowed',
                            }}
                          >
                            {cardInner}
                          </Box>
                        </Tooltip>
                      );
                    }

                    return (
                      <Box key={value} sx={{ width: '100%' }}>
                        {cardInner}
                      </Box>
                    );
                  },
                )}
              </Stack>
            </RadioGroup>
          </>
        ) : null}
      </DialogContent>

      <Divider sx={{ borderColor: borderSubtle }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          disabled={isCreating}
          startIcon={<X size={14} weight='bold' />}
          sx={{
            textTransform: 'none',
            borderColor: borderSubtle,
            color: textPrimary,
            '&:hover': {
              borderColor: isDarkMode ? palette.gray[600] : palette.gray[400],
              bgcolor: cardHoverBg,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant='contained'
          disabled={isCreating}
          startIcon={
            isCreating ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <Plus size={14} weight='bold' />
            )
          }
          sx={{ textTransform: 'none' }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
